import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Button, TextField, Stack, Divider, Paper, Chip, Skeleton,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d + 'T12:00:00').toLocaleDateString('sq-AL', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return d; }
}

// ── Reusable detail row ──────────────────────────────────────────────────────
function DetailRow({ icon, label, value, valueColor, last }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 2,
      px: 2.5, py: 1.75,
      borderBottom: last ? 'none' : '1px solid #f1f5f9',
    }}>
      <Box sx={{ color: '#94a3b8', mt: '1px', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: valueColor || 'text.primary', lineHeight: 1.4 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <Box sx={{ p: 3.5 }}>
      <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={44} />
    </Box>
  );
}

export default function ConsentPage() {
  const { token } = useParams();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState('');
  const [comment, setComment] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(null); // 'agreed' | 'disagreed'

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/consent/${token}`)
      .then(r => r.json())
      .then(r => {
        if (r.success) {
          setData(r.data);
          if (r.data.status !== 'pending') setDone(r.data.status);
        } else {
          setErr(r.error || 'Linku nuk u gjet ose ka skaduar.');
        }
      })
      .catch(() => setErr('Nuk mund të lidhemi me serverin.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (status) => {
    setSaving(true);
    setErr('');
    try {
      const r = await fetch(`${API_URL}/api/consent/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, comment: comment.trim() }),
      }).then(x => x.json());
      if (r.success) {
        setDone(status);
      } else {
        setErr(r.error || 'Gabim gjatë dërgimit.');
      }
    } catch {
      setErr('Nuk mund të lidhemi me serverin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #f8fafc 0%, #eef2f7 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      py: { xs: 4, sm: 6 },
      px: 2,
    }}>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          background: '#1e293b',
          borderRadius: '12px',
          px: 3, py: 2,
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px',
            background: 'rgba(220,38,38,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <GavelIcon sx={{ color: '#f87171', fontSize: 18 }} />
          </Box>
          <Box>
            {data?.companyName ? (
              <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                {data.companyName}
              </Typography>
            ) : (
              <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                Njoftim Zyrtar
              </Typography>
            )}
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.25 }}>
              Departamenti i Burimeve Njerëzore · Vendim Ndërprerje Kontrate
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              label="Konfidencial"
              size="small"
              sx={{
                bgcolor: 'rgba(220,38,38,0.12)', color: '#f87171',
                fontWeight: 600, fontSize: '0.65rem', height: 22,
                border: '1px solid rgba(248,113,113,0.2)',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Main card ───────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          width: '100%', maxWidth: 600,
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(15,23,42,0.06)',
        }}
      >

        {/* Loading */}
        {loading && <LoadingSkeleton />}

        {/* Error — invalid / expired link */}
        {!loading && err && !data && (
          <Box sx={{ py: 7, px: 4, textAlign: 'center' }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              background: '#fef2f2', border: '2px solid #fecaca',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2.5,
            }}>
              <ErrorOutlineIcon sx={{ fontSize: 34, color: '#dc2626' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1e293b', mb: 1 }}>
              Linku nuk është valid
            </Typography>
            <Typography color="text.secondary" fontSize="0.875rem" sx={{ maxWidth: 340, mx: 'auto' }}>
              {err}
            </Typography>
          </Box>
        )}

        {/* Already responded */}
        {!loading && done && (
          <Box sx={{ py: 7, px: 4, textAlign: 'center' }}>
            {done === 'agreed' ? (
              <>
                <Box sx={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: '#f0fdf4', border: '2px solid #bbf7d0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2.5,
                }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 34, color: '#16a34a' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#15803d', mb: 1 }}>
                  Pajtimi u regjistrua
                </Typography>
                <Typography color="text.secondary" fontSize="0.875rem" sx={{ maxWidth: 340, mx: 'auto' }}>
                  Ju u pajtuat me vendimin e ndërprerjes. Qëndrimi juaj u dërgua me sukses tek Departamenti i BNJ.
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: '#fef2f2', border: '2px solid #fecaca',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2.5,
                }}>
                  <CancelOutlinedIcon sx={{ fontSize: 34, color: '#dc2626' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#b91c1c', mb: 1 }}>
                  Mospajtimi u regjistrua
                </Typography>
                <Typography color="text.secondary" fontSize="0.875rem" sx={{ maxWidth: 340, mx: 'auto' }}>
                  Ju nuk u pajtuat me vendimin. Qëndrimi juaj dhe deklarata u dërguan me sukses tek Departamenti i BNJ.
                </Typography>
              </>
            )}

            {data?.comment && (
              <Box sx={{
                mt: 3.5, p: 2.5,
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                textAlign: 'left',
              }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                  Deklarata juaj
                </Typography>
                <Typography fontSize="0.875rem" sx={{ color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {data.comment}
                </Typography>
              </Box>
            )}

            <Box sx={{
              mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
              <Typography fontSize="0.72rem" color="text.disabled">
                Mund të mbyllni këtë dritare.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Main form */}
        {!loading && data && !done && (
          <>
            {/* Alert banner */}
            <Box sx={{
              background: 'linear-gradient(90deg, #fef2f2 0%, #fff5f5 100%)',
              borderBottom: '1px solid #fecaca',
              px: 3, py: 2,
              display: 'flex', alignItems: 'flex-start', gap: 1.5,
            }}>
              <GavelIcon sx={{ color: '#dc2626', fontSize: 18, mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ color: '#b91c1c', fontWeight: 700, fontSize: '0.875rem' }}>
                  Njoftim Zyrtar i Ndërprerjes së Kontratës
                </Typography>
                <Typography sx={{ color: '#9f1239', fontSize: '0.75rem', mt: 0.25 }}>
                  Ky dokument kërkon qëndrimin tuaj zyrtar brenda afatit të parashikuar.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>

              {/* Greeting */}
              <Typography sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.8, mb: 3 }}>
                I/E nderuar/a <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>{data.workerName}</Box>,
                <br />
                Kompania <Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>{data.companyName}</Box> ju
                njofton zyrtarisht për ndërprerjen e marrëdhënies së punës sipas detajeve të mëposhtme.
              </Typography>

              {/* Details card */}
              <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                overflow: 'hidden',
                mb: 3,
                background: '#fff',
              }}>
                <Box sx={{
                  px: 2.5, py: 1.5,
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Të dhënat e vendimit
                  </Typography>
                </Box>

                <DetailRow
                  icon={<PersonOutlineIcon sx={{ fontSize: 17 }} />}
                  label="Punëtori/ja"
                  value={data.workerName}
                />
                <DetailRow
                  icon={<BusinessIcon sx={{ fontSize: 17 }} />}
                  label="Kompania"
                  value={data.companyName}
                />
                <DetailRow
                  icon={<CalendarTodayIcon sx={{ fontSize: 17 }} />}
                  label="Data e ndërprerjes"
                  value={formatDate(data.termDate)}
                  valueColor="#dc2626"
                />
                {data.reason && (
                  <DetailRow
                    icon={<InfoOutlinedIcon sx={{ fontSize: 17 }} />}
                    label="Arsyeja"
                    value={data.reason}
                    last
                  />
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Declaration */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                  Koment ose Deklaratë <Box component="span" sx={{ fontWeight: 400, color: '#94a3b8' }}>(opsionale)</Box>
                </Typography>
                <Typography sx={{ fontSize: '0.77rem', color: '#64748b', mb: 1.5 }}>
                  Mund të lini një koment, sqarim ose deklaratë zyrtare rreth këtij vendimi.
                  Deklarata do t'i dërgohet departamentit të BNJ.
                </Typography>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder="Shkruani komentin ose deklaratën tuaj këtu…"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  disabled={saving}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      borderRadius: '8px',
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                    },
                  }}
                />
              </Box>

              {err && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  p: 1.5, mb: 2.5, borderRadius: '8px',
                  background: '#fef2f2', border: '1px solid #fecaca',
                }}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: '#dc2626', flexShrink: 0 }} />
                  <Typography color="error" fontSize="0.82rem">{err}</Typography>
                </Box>
              )}

              {/* Action buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                  disabled={saving}
                  onClick={() => submit('agreed')}
                  sx={{
                    bgcolor: '#16a34a',
                    '&:hover': { bgcolor: '#15803d' },
                    '&:disabled': { bgcolor: '#86efac' },
                    boxShadow: '0 1px 3px rgba(22,163,74,0.25), 0 4px 12px rgba(22,163,74,0.15)',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    borderRadius: '8px',
                    py: 1.25,
                  }}
                >
                  Pajtohem me vendimin
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />}
                  disabled={saving}
                  onClick={() => submit('disagreed')}
                  sx={{
                    borderColor: '#fca5a5',
                    color: '#dc2626',
                    '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' },
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    borderRadius: '8px',
                    py: 1.25,
                  }}
                >
                  Nuk pajtohem
                </Button>
              </Stack>

              <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                <InfoOutlinedIcon sx={{ fontSize: 13, color: '#cbd5e1' }} />
                <Typography fontSize="0.7rem" color="text.disabled">
                  Ky link është i vlefshëm për 7 ditë. Qëndrimi juaj do t'i dërgohet departamentit të BNJ.
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <Typography sx={{ mt: 4, fontSize: '0.7rem', color: '#cbd5e1', textAlign: 'center' }}>
        © {new Date().getFullYear()} Nexuma · Sistemi i Menaxhimit të Burimeve Njerëzore
      </Typography>
    </Box>
  );
}
